import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select, { components as selectComponents } from 'react-select';
import debounce from 'lodash/debounce';
import { get } from '../../api/axios';
import endpoints from '../../api/endpoints';
import chevronIcon from '../../assets/images/layouts/chevron-sidebar-icon.svg';

const DEBOUNCE_MS = 300;

const mapToOptions = (list = []) =>
  list.map((item) => ({ value: item.id, label: item.name, raw: item }));

const ChevronDropdown = (props) => {
  const isOpen = props.selectProps?.menuIsOpen;
  return (
    <selectComponents.DropdownIndicator {...props}>
      <img
        src={chevronIcon}
        alt=""
        className={`amok-role-select__chevron ${
          isOpen ? 'amok-role-select__chevron--open' : ''
        }`}
      />
    </selectComponents.DropdownIndicator>
  );
};

const customStyles = (hasError = false) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    height: 38,
    border: '1px solid transparent',
    borderColor: hasError ? '#ef4444' : (state.isFocused ? 'rgba(231, 231, 234, 1)' : 'transparent'),
    borderRadius: 12,
    backgroundColor: state.isFocused ? 'rgba(255, 255, 255, 1)' : 'rgba(250, 250, 250, 1)',
    boxShadow: state.isFocused ? "0 0 0 1px rgba(17, 17, 17, 0.06)" : 'none',
    padding: 0,
    fontSize: 14,
    fontWeight: 500,
    color: '#1C1C1C',
    cursor: 'text',
    transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      backgroundColor: '#f2f2f4',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '8px 12px',
    fontSize: 14,
    fontWeight: 500,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: '#1C1C1C',
    fontWeight: 500,
  }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    color: '#1C1C1C',
    fontWeight: 500,
    fontSize: 14,
  }),
  placeholder: (base) => ({
    ...base,
    margin: 0,
    color: 'rgba(110, 110, 110, 0.7)',
    fontWeight: 500,
  }),
  indicatorsContainer: (base) => ({ ...base, padding: '0 12px 0 0', alignItems: 'center' }),
  dropdownIndicator: (base) => ({ ...base, padding: 0, color: 'transparent' }),
  menu: (base) => ({
    ...base,
    marginTop: 8,
    borderRadius: 12,
    boxShadow: '0 8px 24px rgba(17, 17, 17, 0.08)',
    overflow: 'hidden',
    zIndex: 100,
  }),
  menuList: (base) => ({
    ...base,
    padding: 0,
    maxHeight: 200,
  }),
  option: (base, state) => ({
    ...base,
    padding: '8px 12px',
    fontSize: 14,
    fontWeight: 500,
    color: '#1C1C1C',
    backgroundColor: state.isFocused || state.isSelected ? '#f2f2f4' : '#ffffff',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#e7e7ea',
    },
  }),
  loadingMessage: (base) => ({
    ...base,
    padding: '12px',
    fontSize: 14,
    color: '#B7B7B7',
    textAlign: 'center',
  }),
  noOptionsMessage: (base) => ({
    ...base,
    padding: '12px',
    fontSize: 14,
    color: '#B7B7B7',
    textAlign: 'center',
  }),
});

export { customStyles, ChevronDropdown };

const RoleSelect = ({
  label,
  placeholder,
  value = null,
  onChange,
  helperText,
  error,
  required = false,
  onBlur,
  menuPortalTarget = null,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const latestRequest = useRef(0);
  const abortRef = useRef(null);

  const selectedOption = useMemo(
    () => (value ? { value: value.id, label: value.name } : null),
    [value]
  );

  const debouncedSetQuery = useMemo(
    () => debounce((q) => setSearchQuery(q), DEBOUNCE_MS),
    []
  );

  const load = useCallback(
    async ({ query = '', pageUrl = null, isLoadMore = false } = {}) => {
      const requestId = ++latestRequest.current;

      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      if (isLoadMore) {
        setIsLoading(false);
        setIsLoadingMore(true);
      } else {
        setIsLoadingMore(false);
        setIsLoading(true);
      }

      let url =
        pageUrl || `${endpoints.getRoleTypes}?search=${encodeURIComponent(query)}`;

      if (pageUrl && query && !url.includes('search=')) {
        const separator = url.includes('?') ? '&' : '?';
        url = `${url}${separator}search=${encodeURIComponent(query)}`;
      }

      try {
        const res = await get(url, { signal: controller.signal });

        if (requestId !== latestRequest.current) return;

        const result = res?.data?.result || [];
        const list = Array.isArray(result) ? result : result?.data || [];
        const next = Array.isArray(result)
          ? res?.data?.next_page_url
          : result?.next_page_url;
        const mapped = mapToOptions(list);

        if (isLoadMore) {
          setOptions((prev) => [...prev, ...mapped]);
        } else {
          setOptions(mapped);
        }
        setNextPageUrl(next);
      } catch {
        // ignore
      } finally {
        if (requestId !== latestRequest.current) return;
        if (isLoadMore) {
          setIsLoadingMore(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    if (!menuOpen) return;
    load({ query: searchQuery });
  }, [menuOpen, searchQuery, load]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      debouncedSetQuery.cancel();
    };
  }, [debouncedSetQuery]);

  const handleInputChange = useCallback(
    (newValue, { action }) => {
      if (action !== 'input-change') return;
      setInputValue(newValue);
      debouncedSetQuery(newValue);
    },
    [debouncedSetQuery]
  );

  const handleChange = useCallback(
    (option) => {
      const id = option?.value || '';
      const name = option?.label || '';
      setInputValue('');
      setSearchQuery('');
      onChange?.({ id, name });
      setMenuOpen(false);
    },
    [onChange]
  );

  const handleMenuOpen = useCallback(() => {
    setMenuOpen(true);
    setSearchQuery(inputValue);
    debouncedSetQuery.cancel();
  }, [inputValue, debouncedSetQuery]);

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false);
    onBlur?.();
  }, [onBlur]);

  const handleScrollToBottom = useCallback(() => {
    if (isLoading || isLoadingMore || !nextPageUrl) return;
    load({ query: searchQuery, pageUrl: nextPageUrl, isLoadMore: true });
  }, [isLoading, isLoadingMore, nextPageUrl, load, searchQuery]);

  const MenuList = useCallback(
    (props) => (
      <selectComponents.MenuList {...props}>
        {props.children}
        {props.selectProps?.isLoadingMore && (
          <div
            className="amok-skeleton amok-role-select__loading-more"
            aria-hidden="true"
          />
        )}
      </selectComponents.MenuList>
    ),
    []
  );

  return (
    <div className={`amok-field ${error ? 'amok-field--error' : ''}`}>
      {label && (
        <label className="amok-field__label">
          {label}
          {required && <span className="amok-field__required"> *</span>}
        </label>
      )}
      <Select
        className={`amok-role-select ${error ? 'amok-role-select--error' : ''}`}
        classNamePrefix="amok-role-select"
        inputValue={inputValue}
        onInputChange={handleInputChange}
        value={selectedOption}
        onChange={handleChange}
        options={options}
        isSearchable
        isClearable={false}
        openMenuOnFocus
        openMenuOnClick
        menuIsOpen={menuOpen}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        onMenuScrollToBottom={handleScrollToBottom}
        placeholder={placeholder}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        components={{
          DropdownIndicator: ChevronDropdown,
          IndicatorSeparator: null,
          MenuList,
        }}
        noOptionsMessage={() => 'No roles found'}
        loadingMessage={() => 'Loading...'}
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPortalTarget ? 'fixed' : undefined}
        styles={{
          ...customStyles(!!error),
          ...(menuPortalTarget
            ? { menuPortal: (base) => ({ ...base, zIndex: 2000 }) }
            : {}),
        }}
      />
      {error && (
        <p className="amok-field__message amok-field__message--error">{error}</p>
      )}
      {!error && helperText && (
        <p className="amok-field__message">{helperText}</p>
      )}
    </div>
  );
};

export default RoleSelect;
